import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TailAdminStatusBadge } from '@/components/template/tailadmin/DashboardPrimitives';


const LABELS = {
  identifier_lookup: 'Identifier lookup',
  registration: 'Registration',
  subscription_lookup: 'Subscription lookup',
  conversion_webhook: 'Conversion webhook',
  audience_lookup: 'Audience lookup',
  custom_task: 'Custom task',
};
const SUPPORTED_PURPOSES = Object.keys(LABELS);

const CONTRACT_TEMPLATES = {
  identifier_lookup: {
    requestMapping: { identifier: 'identifier' },
    responseMapping: { external_customer_ref: 'data.customer_id' },
    testPayload: { identifier: 'replace-with-valid-test-identifier' },
    expectedResponse: { external_customer_ref: 'replace-with-expected-customer-id' },
  },
  registration: {
    requestMapping: { registration_data: 'fields' },
    responseMapping: { external_customer_ref: 'data.customer_id' },
    testPayload: { registration_data: { email: 'test@example.com', name: 'Test Customer' } },
    expectedResponse: { external_customer_ref: 'replace-with-expected-customer-id' },
  },
  subscription_lookup: {
    requestMapping: { external_customer_ref: 'customer_id' },
    responseMapping: { qualified: 'data.subscribed', subscription_date: 'data.subscription_date' },
    testPayload: { external_customer_ref: 'replace-with-valid-subscribed-customer-id' },
    expectedResponse: { qualified: true },
  },
};

const jsonText = (value) => JSON.stringify(value || {}, null, 2);

const TypedContractBuilder = ({
  contracts = [],
  reusableContracts = [],
  requiredPurposes = [],
  activeEnvironment,
  onSave,
  onTest,
  onBind,
  saving = false,
  showReadiness = true,
}) => {
  const firstPurpose = requiredPurposes[0] || 'subscription_lookup';
  const selectedEnvironment = activeEnvironment || '';
  const initialTemplate = CONTRACT_TEMPLATES[firstPurpose] || {};
  const [form, setForm] = useState({
    name: selectedEnvironment
      ? `${LABELS[firstPurpose] || firstPurpose} (${selectedEnvironment})`
      : (LABELS[firstPurpose] || firstPurpose),
    purpose: firstPurpose,
    environment: selectedEnvironment,
    baseUrl: '',
    path: '',
    method: firstPurpose === 'registration' ? 'POST' : 'GET',
    authType: '',
    secret: '',
    requestMapping: jsonText(initialTemplate.requestMapping),
    responseMapping: jsonText(initialTemplate.responseMapping),
    testPayload: jsonText(initialTemplate.testPayload),
    invalidTestPayload: '',
    expectedResponse: jsonText(initialTemplate.expectedResponse),
    acceptedStatusCodes: '200, 201',
    successPredicate: '',
    timeoutMs: 5000,
    maxRetries: 0,
    retryBackoffMs: 250,
    idempotencyMode: 'proovam_header',
    customHeaders: '{}',
  });
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (!form.purpose && firstPurpose) {
      setForm((current) => ({ ...current, purpose: firstPurpose }));
    }
  }, [firstPurpose, form.purpose]);

  useEffect(() => {
    if (!activeEnvironment) return;
    setForm((current) => {
      if (current.environment === activeEnvironment) return current;
      const previousSuffix = ` (${current.environment})`;
      const name = current.name.endsWith(previousSuffix)
        ? `${current.name.slice(0, -previousSuffix.length)} (${activeEnvironment})`
        : current.name;
      return { ...current, environment: activeEnvironment, name };
    });
  }, [activeEnvironment]);

  const missing = useMemo(() => requiredPurposes.filter((purpose) => (
    !contracts.some((contract) => contract.purpose === purpose && contract.environment === selectedEnvironment && contract.status === 'verified')
  )), [contracts, requiredPurposes, selectedEnvironment]);
  const selectablePurposes = requiredPurposes.length
    ? [...new Set([...requiredPurposes, 'audience_lookup'])]
    : SUPPORTED_PURPOSES;

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const changePurpose = (purpose) => {
    const template = CONTRACT_TEMPLATES[purpose] || {};
    setJsonError('');
    setForm((current) => ({
      ...current,
      name: `${LABELS[purpose] || purpose} (${current.environment})`,
      purpose,
      method: purpose === 'registration' ? 'POST' : 'GET',
      requestMapping: jsonText(template.requestMapping),
      responseMapping: jsonText(template.responseMapping),
      testPayload: jsonText(template.testPayload),
      expectedResponse: jsonText(template.expectedResponse),
    }));
  };

  const save = () => {
    try {
      const {
        requestMapping, responseMapping, testPayload, invalidTestPayload,
        expectedResponse, successPredicate, customHeaders, acceptedStatusCodes, ...rest
      } = form;
      const statusTokens = acceptedStatusCodes
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      const statusCodes = statusTokens.map(Number);
      if (
        !statusCodes.length
        || statusCodes.some((value) => !Number.isInteger(value) || value < 100 || value > 599)
      ) {
        throw new Error('invalid status code');
      }
      const payload = {
        ...rest,
        environment: activeEnvironment,
        authType: form.authType || null,
        secret: form.secret || undefined,
        requestMapping: JSON.parse(requestMapping || '{}'),
        responseMapping: JSON.parse(responseMapping || '{}'),
        testPayload: JSON.parse(testPayload || '{}'),
        invalidTestPayload: invalidTestPayload.trim() ? JSON.parse(invalidTestPayload) : undefined,
        expectedResponse: JSON.parse(expectedResponse || '{}'),
        acceptedStatusCodes: statusCodes,
        successPredicate: successPredicate.trim() ? JSON.parse(successPredicate) : undefined,
        customHeaders: JSON.parse(customHeaders || '{}'),
      };
      setJsonError('');
      onSave(payload);
    } catch {
      setJsonError('Check the JSON fields and accepted status codes before saving.');
    }
  };

  return (
    <div className="space-y-5">
      {showReadiness && <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Publication contract gate</p>
          <p className="text-xs text-gray-500">
            {missing.length === 0 ? 'All required contracts are verified.' : `${missing.length} required contracts remaining.`}
          </p>
        </div>
        <TailAdminStatusBadge tone={missing.length === 0 ? 'success' : 'warning'}>
          {missing.length === 0 ? 'Ready' : 'Not ready'}
        </TailAdminStatusBadge>
      </div>}

      <details className="rounded-xl border border-gray-200 bg-white p-4">
        <summary className="cursor-pointer rounded-lg text-sm font-semibold text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2">Developer settings · endpoint, mapping and retries</summary>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="md:col-span-2">
          <Label htmlFor="contract-name">Reusable contract name</Label>
          <Input id="contract-name" value={form.name} onChange={(event) => update('name', event.target.value)} placeholder="Customer lookup (production)" />
          <p className="mt-1 text-xs text-gray-500">This Project asset can be bound to more than one draft Proove Request.</p>
        </div>
        <div>
          <Label htmlFor="contract-purpose">Purpose</Label>
          <select id="contract-purpose" value={form.purpose} onChange={(event) => changePurpose(event.target.value)} className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
            {selectablePurposes.map((purpose) => (
              <option key={purpose} value={purpose}>{LABELS[purpose] || purpose}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="contract-environment">Environment</Label>
          <select id="contract-environment" value={form.environment} onChange={(event) => update('environment', event.target.value)} disabled={Boolean(activeEnvironment)} className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm disabled:bg-gray-100">
            {!activeEnvironment && <option value="">Waiting for payment mode…</option>}
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {activeEnvironment
              ? `Payment mode locks this contract to ${activeEnvironment}.`
              : 'Contract editing is unavailable until the active payment mode loads.'}
          </p>
        </div>
        <div>
          <Label htmlFor="contract-base-url">Base URL</Label>
          <Input id="contract-base-url" type="url" value={form.baseUrl} onChange={(event) => update('baseUrl', event.target.value)} placeholder="https://api.example.com" />
        </div>
        <div>
          <Label htmlFor="contract-path">Path</Label>
          <Input id="contract-path" value={form.path} onChange={(event) => update('path', event.target.value)} placeholder="/v1/customers/verify" />
        </div>
        <div>
          <Label htmlFor="contract-method">Method</Label>
          <select id="contract-method" value={form.method} onChange={(event) => update('method', event.target.value)} className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
          </select>
        </div>
        <div>
          <Label htmlFor="contract-auth-type">Authentication</Label>
          <select id="contract-auth-type" value={form.authType} onChange={(event) => update('authType', event.target.value)} className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
            <option value="">None</option>
            <option value="api_key">API key</option>
            <option value="jwt">Bearer token</option>
            <option value="basic">Basic auth</option>
            <option value="oauth2_client_credentials">OAuth2 client credentials</option>
            <option value="hmac_sha256">HMAC SHA-256</option>
          </select>
        </div>
        {form.authType && (
          <div className="md:col-span-2">
            <Label htmlFor="contract-secret">Credential</Label>
            <Input id="contract-secret" type="password" autoComplete="new-password" value={form.secret} onChange={(event) => update('secret', event.target.value)} placeholder="Stored encrypted; never shown again" />
            {form.authType === 'oauth2_client_credentials' && <p className="mt-1 text-xs text-gray-500">Enter JSON with token_url, client_id, client_secret, and optional scope.</p>}
          </div>
        )}
        {[
          ['requestMapping', 'Request mapping', 'Map Proovam fields to provider request paths; dot paths such as registration_data.email are supported.'],
          ['responseMapping', 'Response mapping', 'Map provider response paths to Proovam fields.'],
          ['testPayload', 'Test payload', 'Use a real sandbox-safe test customer or identifier.'],
          ['invalidTestPayload', 'Known-invalid test payload (optional)', 'A negative fixture must fail qualification; it prevents false-positive contract tests.'],
          ['expectedResponse', 'Expected mapped response', 'The mapped fields required for this test to pass.'],
          ['successPredicate', 'Success predicate (optional)', 'JSON with path and equals, for example {"path":"data.active","equals":true}.'],
          ['customHeaders', 'Safe custom headers', 'JSON object only. Host, forwarding, metadata, and hop-by-hop headers are blocked.'],
        ].map(([key, label, hint]) => (
          <div key={key} className="md:col-span-2">
            <Label htmlFor={`contract-${key}`}>{label}</Label>
            <textarea
              id={`contract-${key}`}
              value={form[key]}
              onChange={(event) => update(key, event.target.value)}
              rows={4}
              spellCheck={false}
              className="mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-xs"
            />
            <p className="mt-1 text-xs text-gray-500">{hint}</p>
          </div>
        ))}
        <div>
          <Label htmlFor="contract-status-codes">Accepted status codes</Label>
          <Input id="contract-status-codes" value={form.acceptedStatusCodes} onChange={(event) => update('acceptedStatusCodes', event.target.value)} placeholder="200, 201" />
        </div>
        <div>
          <Label htmlFor="contract-timeout">Timeout (milliseconds)</Label>
          <Input id="contract-timeout" type="number" min="250" max="15000" value={form.timeoutMs} onChange={(event) => update('timeoutMs', Number(event.target.value))} />
        </div>
        <div>
          <Label htmlFor="contract-retries">Maximum retries</Label>
          <Input id="contract-retries" type="number" min="0" max="3" value={form.maxRetries} onChange={(event) => update('maxRetries', Number(event.target.value))} />
        </div>
        <div>
          <Label htmlFor="contract-backoff">Initial retry backoff (milliseconds)</Label>
          <Input id="contract-backoff" type="number" min="50" max="2000" value={form.retryBackoffMs} onChange={(event) => update('retryBackoffMs', Number(event.target.value))} />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="contract-idempotency">Retry idempotency</Label>
          <select id="contract-idempotency" value={form.idempotencyMode} onChange={(event) => update('idempotencyMode', event.target.value)} className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
            <option value="proovam_header">Send a stable Proovam idempotency header</option>
            <option value="none">No idempotency header (unsafe methods cannot retry)</option>
          </select>
        </div>
        {jsonError && <p className="text-sm text-red-600 md:col-span-2">{jsonError}</p>}
        <div className="md:col-span-2">
          <Button type="button" onClick={save} disabled={saving || !activeEnvironment || !form.baseUrl || !form.path}>
            {saving ? 'Saving…' : 'Save contract'}
          </Button>
        </div>
      </div>
      </details>

      {contracts.length > 0 && (
        <div className="space-y-2">
          {contracts.map((contract) => (
            <div key={contract.contract_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 p-3">
              <div>
                <p className="text-sm font-semibold">{LABELS[contract.purpose] || contract.purpose}</p>
                <p className="text-xs text-gray-500">{contract.method} {contract.base_url}{contract.path}</p>
              </div>
              <div className="flex items-center gap-2">
                <TailAdminStatusBadge tone={contract.status === 'verified' ? 'success' : contract.status === 'failed' ? 'danger' : 'neutral'}>{contract.status}</TailAdminStatusBadge>
                {onTest && <Button type="button" size="sm" variant="outline" onClick={() => onTest(contract.contract_id)}>Test</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeEnvironment && reusableContracts.some((candidate) => (
        candidate.environment === selectedEnvironment
        &&
        !contracts.some((bound) => bound.contract_id === candidate.contract_id)
      )) && (
        <div className="space-y-2 rounded-xl border border-brand-100 bg-brand-50/40 p-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Project contract library</p>
            <p className="text-xs text-gray-600">Test a draft revision in this request, or bind an already verified immutable revision.</p>
          </div>
          {reusableContracts.filter((candidate) => (
            candidate.environment === selectedEnvironment
            &&
            !contracts.some((bound) => bound.contract_id === candidate.contract_id)
          )).map((contract) => (
            <div key={contract.contract_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-100 bg-white p-3">
              <div>
                <p className="text-sm font-semibold">{contract.name || LABELS[contract.purpose] || contract.purpose}</p>
                <p className="text-xs text-gray-500">{LABELS[contract.purpose] || contract.purpose} · revision {contract.current_revision}</p>
              </div>
              <div className="flex items-center gap-2">
                <TailAdminStatusBadge tone={contract.status === 'verified' ? 'success' : 'neutral'}>{contract.status}</TailAdminStatusBadge>
                {contract.status === 'verified' ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => onBind?.(contract.contract_id)} disabled={saving}>Reuse contract</Button>
                ) : (
                  <Button type="button" size="sm" variant="outline" onClick={() => onTest?.(contract.contract_id)} disabled={saving}>Test and bind</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TypedContractBuilder;
